import React from "react";
import { getImageUrl } from "../../api/client";
import HeroSection from "../Home/Hero/HeroSection";
import HowItWorks from "../Home/HowItWorks/HowItWorks";
import NewSeason from "../Home/NewSeason/NewSeason";
import HotListPromo from "../Home/HotListPromo/HotListPromo";
import CategoryGrid from "../Home/CategoryGrid/CategoryGrid";
import Instagram from "../Home/Instagram/Instagram";
import NewsletterBlock from "../Home/NewsletterBlock/NewsletterBlock";

const BLOCK_MAP = {
  hero: HeroSection,
  how_it_works: HowItWorks,
  new_season: NewSeason,
  hot_list: HotListPromo,
  category_grid: CategoryGrid,
  instagram: Instagram,
  newsletter: NewsletterBlock,
  text: TextBlock,
  image: ImageBlock,
  button: ButtonBlock,
  video: VideoBlock,
  product_grid: ProductGridBlock,
  custom_html: CustomHtmlBlock,
};

function TextBlock({ props: p }) {
  if (!p?.html && !p?.markdown) return null;
  return (
    <section className="block-text" style={{ maxWidth: p?.maxWidth || 720, margin: "0 auto", padding: "1rem" }}>
      <div dangerouslySetInnerHTML={{ __html: p.html || p.markdown }} />
    </section>
  );
}

function ImageBlock({ props: p }) {
  if (!p?.url) return null;
  const img = <img src={getImageUrl(p.url) || p.url} alt={p.alt || ""} />;
  return (
    <section className="block-image" style={{ textAlign: p?.alignment || "center", padding: "1rem" }}>
      {p.link ? <a href={p.link}>{img}</a> : img}
      {p.caption && <p style={{ marginTop: "0.5rem", fontSize: "0.9rem" }}>{p.caption}</p>}
    </section>
  );
}

function resolveVideoSource(value) {
  const raw = String(value || "").trim();
  if (!raw) return null;
  if (raw.startsWith("/")) return { src: getImageUrl(raw) || raw, embed: false };
  try {
    const url = new URL(raw);
    if (url.protocol !== "https:") return null;
    const host = url.hostname.toLowerCase().replace(/^www\./, "");
    if (host === "youtu.be") {
      const id = url.pathname.split("/").filter(Boolean)[0];
      return id ? { src: `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}`, embed: true } : null;
    }
    if (host === "youtube.com" || host === "m.youtube.com") {
      const id = url.pathname.startsWith("/embed/")
        ? url.pathname.split("/").filter(Boolean)[1]
        : url.searchParams.get("v");
      return id ? { src: `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}`, embed: true } : null;
    }
    if (host === "vimeo.com" || host === "player.vimeo.com") {
      const id = url.pathname.split("/").filter(Boolean).find((part) => /^\d+$/.test(part));
      return id ? { src: `https://player.vimeo.com/video/${id}`, embed: true } : null;
    }
    return { src: url.toString(), embed: false };
  } catch (_) {
    return null;
  }
}

function VideoBlock({ props: p }) {
  const source = resolveVideoSource(p?.url);
  if (!source) return null;
  const title = p.title || "Featured video";
  return (
    <section className="block-video" style={{ padding: "2rem 1rem", textAlign: "center" }}>
      {title && <h2>{title}</h2>}
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        {source.embed ? (
          <iframe title={title} src={source.src} style={{ width: "100%", aspectRatio: "16 / 9", border: 0 }} allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen />
        ) : (
          <video controls preload="metadata" poster={p.poster ? (getImageUrl(p.poster) || p.poster) : undefined} style={{ width: "100%", maxHeight: 540 }}>
            <source src={source.src} />
          </video>
        )}
      </div>
      {p.caption && <p style={{ marginTop: "0.75rem", color: "#666" }}>{p.caption}</p>}
    </section>
  );
}

function ButtonBlock({ props: p }) {
  if (!p?.text) return null;
  return (
    <section className="block-button" style={{ textAlign: "center", padding: "1rem" }}>
      <a href={p.link || "#"} className={p.variant === "outline" ? "btn-outline" : "btn-primary"}>
        {p.text}
      </a>
    </section>
  );
}

function ProductGridBlock({ props: p }) {
  return (
    <section className="block-product-grid" style={{ padding: "1rem" }}>
      {p?.title && <h3>{p.title}</h3>}
      <p style={{ color: "#666" }}>Product grid (source: {p?.source || "featured"}) – wire to products API when needed.</p>
    </section>
  );
}

function CustomHtmlBlock({ props: p }) {
  if (!p?.html) return null;
  return <section className="block-custom-html" dangerouslySetInnerHTML={{ __html: p.html }} />;
}

export default function BlockRenderer({ content, onBlockClick, selectedBlockIndex }) {
  if (!Array.isArray(content) || content.length === 0) return null;
  return (
    <>
      {content.map((block, index) => {
        const Component = BLOCK_MAP[block.type];
        if (!Component) return null;
        const blockKey = block.id || `${block.type}-${index}`;
        const el = <Component key={blockKey} props={block.props || {}} />;
        if (typeof onBlockClick === "function") {
          const isSelected = selectedBlockIndex === index;
          return (
            <div
              key={blockKey}
              onClick={() => onBlockClick(index)}
              className={isSelected ? "selected" : undefined}
              style={{ cursor: "pointer" }}
              data-block-index={index}
            >
              {el}
            </div>
          );
        }
        return el;
      })}
    </>
  );
}
